'use client';

import { LoadingSpinnerIcon } from '@/assets/icons/LoadingSpinner';
import { TextInput } from '@/components/inputs/TextInput';
import { PopUpProps } from '@/types/shared';
import { cookieHelper, CookieHelper } from '@/utils/cookies';
import { pushToDataLayer } from '@/utils/dataLayer';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import {
  useActionState,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button } from '../Button';
import { Modal } from '../Modal';

const ONE_MONTH = 30;
const ONE_DAY_IN_SECONDS = 24 * 60 * 60;
// This text is hard-coded because `useTranslations` doesn’t work across the Server→Client boundary.
// Even though <NextIntlClientProvider> wraps this component in the layout, server components
// can’t access client context, so the translation context isn’t available and causes a build-time error.
const CONSENT_COPY =
  'By submitting your email address, you agree to receive occasional marketing communications and promotions from Sollis. You can unsubscribe at any time.';

interface FormState {
  success: boolean | null;
  message: string | null;
}

async function submit(previousState: FormState, formData: FormData) {
  // Don't try and submit if already submitted
  if (previousState?.success) return previousState;

  const email = formData.get('email');
  const payload = {
    email,
    didConsentToMarketing: true,
  };

  try {
    const res = await fetch('/api/create-mql', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      console.error('Failed to submit MQL');
      return {
        success: false,
        message: 'Something went wrong, please try again.',
      };
    }
    // Fire 'submitted' event to GTM
    pushToDataLayer({ eventName: 'PopUpFormSubmitted' });

    return { success: true, message: 'Thank you for subscribing!' };
  } catch (error) {
    console.error('Error submitting MQL:', error);
    return {
      success: false,
      message: 'Network error. Please try again later.',
    };
  }
}

export function PopUp({
  buttonCopy,
  image,
  title,
  excludedPages,
  delay,
}: PopUpProps) {
  const currentPath = usePathname();
  const isOnExcludedPage = useMemo(
    () => (excludedPages || []).some(path => path.pathname === currentPath),
    [excludedPages, currentPath]
  );

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [lastPath, setLastPath] = useState(currentPath);

  // Close popup when navigating to excluded pages during render
  if (lastPath !== currentPath) {
    setLastPath(currentPath);
    if (isOnExcludedPage && isOpen) {
      setIsOpen(false);
    }
  }
  const [state, formAction, isPending] = useActionState(submit, {
    success: null,
    message: null,
  });

  // When the user clicks the no thanks link, we want to temporarily hide the
  // the popup until the next session.
  const handleModalClose = useCallback(() => {
    if (!state.success) {
      sessionStorage.setItem('Popup dismissed', 'true');
    }
    // Fire 'dismissed' event to GTM
    pushToDataLayer({ eventName: 'PopUpDismissed' });

    setIsOpen(false);
  }, [state.success]);

  useEffect(() => {
    // When the popup is opened, fire an event
    if (isOpen) {
      // Fire 'shown' event to GTM
      pushToDataLayer({ eventName: 'PopUpShown' });
    }
  }, [isOpen]);

  useEffect(() => {
    // If we're on an excluded page, do nothing.
    if (isOnExcludedPage) return;

    // We read from document.cookie and sessionStorage here instead of in
    // useState, because these browser APIs are not available during
    // server-side rendering. Putting this logic inside useEffect ensures it
    // only runs client-side, after the component has mounted in the browser.
    const cookieHelper = new CookieHelper(document.cookie);
    const dismissed = sessionStorage.getItem('Popup dismissed');
    const popupSubmitted = cookieHelper.get('Popup submitted');

    // If the user has dismissed or submitted the popup, do nothing.
    if (dismissed || popupSubmitted) return;

    // Add a CMS configurable delay before showing the pop-up.
    setTimeout(() => {
      setIsOpen(true);
    }, delay * 1000);
  }, [delay, isOnExcludedPage]);

  // When a user submits the form, we want to hide the popup for a month.
  useEffect(() => {
    if (!state.success) return;

    if (cookieHelper) {
      const expiryDate = ONE_MONTH * ONE_DAY_IN_SECONDS;
      cookieHelper.set('Popup submitted', 'true', { maxAge: expiryDate });
    }

    // We want the popup to automatically close after the form has been
    // submitted.
    setTimeout(handleModalClose, 5000);
  }, [state.success, handleModalClose]);

  return (
    isOpen && (
      <Modal
        isOpen={isOpen}
        onClose={handleModalClose}
        showCloseButton={true}
        backgroundColor='bg-sh-paper'
      >
        {/* Relative wrapper to allow absolute image */}
        <div className='relative min-h-[380px] sm:flex sm:flex-row'>
          {/* Left column: image */}
          {image && (
            <div className='relative hidden w-1/2 sm:block'>
              <Image
                src={image}
                alt={title}
                fill
                className='w-full overflow-hidden object-cover'
              />
            </div>
          )}

          {/* Right column: content */}
          <div className='flex w-full flex-col px-6 py-11 sm:w-1/2 sm:pl-8 xl:px-9 xl:py-22'>
            <form className='text-sh-dark-gray space-y-7.5' action={formAction}>
              <div className='sh-heading-2-serif'>{title}</div>

              {!state.success && (
                <div className='flex flex-col gap-y-2'>
                  <TextInput
                    name='email'
                    placeholder='Enter your Email'
                    type='email'
                    required
                  />
                  <p className='text-xs'>{CONSENT_COPY}</p>
                </div>
              )}

              {isPending && <LoadingSpinnerIcon />}

              {state.message && (
                <p className='sh-body' data-testid='message'>
                  {state.message}
                </p>
              )}

              {!state.success && !isPending && (
                <div className='flex flex-col gap-y-5 text-center'>
                  <Button
                    type='submit'
                    data-testid='button'
                    variant='dark'
                    className='relative'
                  >
                    {buttonCopy}
                  </Button>

                  <a
                    href='#'
                    data-testid='dismiss'
                    onClick={handleModalClose}
                    className='sh-small font-medium underline'
                  >
                    No thanks
                  </a>
                </div>
              )}
            </form>
          </div>
        </div>
      </Modal>
    )
  );
}
